import { lazy, type ComponentType } from "react";

/**
 * React.lazy wrapper that retries on chunk-load failures and forces a hard
 * reload if the chunk is permanently gone (e.g. after a new deployment
 * invalidates the previous hashed asset filename).
 */
export function lazyRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  retries = 2
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    const reloadKey = `lazyRetry:reloaded:${factory.toString().slice(0, 80)}`;

    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const mod = await factory();
        // Reset the reload guard on success
        try { sessionStorage.removeItem(reloadKey); } catch {}
        return mod;
      } catch (err: any) {
        const msg = String(err?.message || err);
        const isChunkError =
          msg.includes("Failed to fetch dynamically imported module") ||
          msg.includes("Importing a module script failed") ||
          msg.includes("error loading dynamically imported module");

        if (!isChunkError || attempt === retries) {
          // Last attempt: force one hard reload to fetch the new manifest
          if (isChunkError || attempt === retries) {
            console.error(`lazyRetry failed after ${attempt} attempts:`, err);
          }
          if (isChunkError) {
            try {
              const alreadyReloaded = sessionStorage.getItem(reloadKey);
              if (!alreadyReloaded) {
                sessionStorage.setItem(reloadKey, "1");
                window.location.reload();
                // Return a never-resolving promise while reload happens
                return await new Promise<{ default: T }>(() => {});
              }
            } catch {}
          }
          throw err;
        }
        // Wait briefly before retrying
        await new Promise((r) => setTimeout(r, 300 * (attempt + 1)));
      }
    }
    // Should never reach here
    throw new Error("lazyRetry: exhausted retries");
  });
}
